/* =====================================================================
   DIGITAL TWIN — DATA ADAPTER

   The UI never imports sample data directly. It asks this adapter for a
   "snapshot" and gets back one of these states:

     { status: "loading" }
     { status: "ready",        snapshot, receivedAt }
     { status: "stale",        snapshot, receivedAt }   data older than STALE_AFTER_MS
     { status: "empty" }                                 source has no lines
     { status: "disconnected", snapshot?, error }        source unreachable

   TODAY:   mode "mock"  → returns src/twin/sampleData.js (always works,
            no backend needed).
   LATER:   mode "poll"  → GET {REACT_APP_BACKEND_URL}/api/twin/snapshot every
            POLL_MS (PROPOSED endpoint, not in backend/server.py yet).
            mode "stream" → replace startPolling() with a WebSocket or
            Server-Sent Events subscription that pushes the same snapshot
            shape. See docs/API_CONTRACTS.md.

   Choose with REACT_APP_TWIN_SOURCE=mock|poll  (default mock). Changing it
   needs a rebuild (React reads REACT_APP_* at build time).

   Testing the states without a backend: add ?twin=loading, ?twin=stale,
   ?twin=empty or ?twin=disconnected to the page address.
   ===================================================================== */

import { SAMPLE_SNAPSHOT } from "./sampleData";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const TWIN_SOURCE = process.env.REACT_APP_TWIN_SOURCE === "poll" && BACKEND_URL ? "poll" : "mock";
export const POLL_MS = 15000;
export const STALE_AFTER_MS = 60000;

const forced = () => {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get("twin");
  return ["loading", "stale", "empty", "disconnected"].includes(v) ? v : null;
};

/** Minimal shape check so a bad API response shows "disconnected" instead of crashing. */
export function isValidSnapshot(s) {
  return !!(s && Array.isArray(s.lines) && s.shift && Array.isArray(s.scenarios)
    && s.lines.every((l) => l.id && Array.isArray(l.stations) && l.stations.every((st) => st.smv > 0 && st.operators >= 0)));
}

async function fetchSnapshot(signal) {
  const res = await fetch(`${BACKEND_URL}/api/twin/snapshot`, { signal, headers: { Accept: "application/json" } });
  if (!res.ok) throw new Error(`twin_http_${res.status}`);
  const json = await res.json();
  if (!isValidSnapshot(json)) throw new Error("twin_bad_shape");
  return json;
}

/**
 * Subscribe to snapshots. Calls onState(state) whenever something changes.
 * Returns an unsubscribe function.
 */
export function subscribeTwin(onState) {
  const f = forced();
  if (f === "loading") { onState({ status: "loading" }); return () => {}; }
  if (f === "empty") { onState({ status: "empty" }); return () => {}; }
  if (f === "disconnected") { onState({ status: "disconnected", snapshot: SAMPLE_SNAPSHOT, error: "forced" }); return () => {}; }
  if (f === "stale") { onState({ status: "stale", snapshot: SAMPLE_SNAPSHOT, receivedAt: Date.now() - 5 * 60000 }); return () => {}; }

  if (TWIN_SOURCE === "mock") {
    onState({ status: "ready", snapshot: SAMPLE_SNAPSHOT, receivedAt: Date.now() });
    return () => {};
  }

  // ---- polling (for a future backend) ----
  let last = null;
  let stopped = false;
  let ctrl = null;
  onState({ status: "loading" });
  const tick = async () => {
    ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 10000);
    try {
      const snapshot = await fetchSnapshot(ctrl.signal);
      last = { snapshot, receivedAt: Date.now() };
      if (!stopped) onState(snapshot.lines.length ? { status: "ready", ...last } : { status: "empty" });
    } catch (error) {
      if (stopped) return;
      if (last && Date.now() - last.receivedAt < STALE_AFTER_MS) onState({ status: "stale", ...last });
      else onState({ status: "disconnected", snapshot: last?.snapshot, error: String(error?.message || error) });
    } finally {
      clearTimeout(timer);
    }
  };
  tick();
  const id = setInterval(tick, POLL_MS);
  return () => { stopped = true; clearInterval(id); ctrl?.abort(); };
}
